'use client'
/**
 * Cart state — server-backed via the /api/cart proxy (Redis on the backend,
 * keyed by the session cookie). We keep the latest CartView in React state and
 * refresh it from each mutation's response, so the server is always the source
 * of truth (no localStorage/Redis divergence). A cached snapshot in
 * localStorage gives an instant first paint before the GET resolves.
 *
 * Checkout: `checkout()` posts to /api/cart/checkout, which creates a real
 * Order and opens a ZarinPal payment session, returning the gateway URL. The
 * caller redirects the browser there; the cart is cleared server-side only
 * after the payment is verified on the return callback.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export type CartLine = {
  product_id: string
  slug: string
  name: string
  unit: string | null
  qty: number
  price_toman: number | null
  line_total_toman: number | null
  image_url: string | null
  in_stock: boolean
}

export type CartView = {
  items: CartLine[]
  total_toman: number | null
  count: number
}

export type CheckoutPayload = {
  contact_name?: string
  contact_phone?: string
  contact_email?: string
  note?: string
  locale?: string
  address_id?: string
}

/** Checkout creates a pending Order and clears the cart; the response carries
 * its id + MRN number. The caller navigates to /checkout/{order_id}. */
export type CheckoutResult = {
  order_id: string
  order_number: number
}

/** checkout() outcome: the created order, 'unauthorized' (guest → send to
 * login), or null on any other failure. */
export type CheckoutOutcome = CheckoutResult | 'unauthorized' | null

type CartCtx = {
  cart: CartView
  ready: boolean
  busy: boolean
  isOpen: boolean
  open: () => void
  close: () => void
  addBySlug: (slug: string, qty?: number) => Promise<void>
  setQty: (productId: string, qty: number) => Promise<void>
  removeItem: (productId: string) => Promise<void>
  clear: () => Promise<void>
  checkout: (payload: CheckoutPayload) => Promise<CheckoutOutcome>
}

const EMPTY: CartView = { items: [], total_toman: null, count: 0 }
const STORAGE_KEY = 'mrn-cart-cache'

const CartContext = createContext<CartCtx | null>(null)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartView>(EMPTY)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  // Cache slug → product_id so addBySlug needs at most one catalog lookup each.
  const slugToId = useRef<Map<string, string>>(new Map())

  const persist = useCallback((view: CartView) => {
    setCart(view)
    for (const l of view.items) slugToId.current.set(l.slug, l.product_id)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(view))
    } catch {
      /* ignore */
    }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/cart', { cache: 'no-store' })
      if (res.ok) persist((await res.json()) as CartView)
    } catch {
      /* offline — keep cached */
    } finally {
      setReady(true)
    }
  }, [persist])

  // Instant paint from the cached snapshot, then reconcile with the server.
  useEffect(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY)
      if (cached) setCart(JSON.parse(cached) as CartView)
    } catch {
      /* ignore */
    }
    void refresh()
  }, [refresh])

  const resolveId = useCallback(async (slug: string): Promise<string | null> => {
    const cached = slugToId.current.get(slug)
    if (cached) return cached
    try {
      const res = await fetch(`/api/catalog/products/${encodeURIComponent(slug)}`, {
        cache: 'no-store',
      })
      if (!res.ok) return null
      const p = (await res.json()) as { id: string }
      slugToId.current.set(slug, p.id)
      return p.id
    } catch {
      return null
    }
  }, [])

  const setQty = useCallback(
    async (productId: string, qty: number) => {
      setBusy(true)
      try {
        const res = await fetch(`/api/cart/items/${productId}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ qty }),
        })
        if (res.ok) persist((await res.json()) as CartView)
      } finally {
        setBusy(false)
      }
    },
    [persist],
  )

  const addBySlug = useCallback(
    async (slug: string, qty = 1) => {
      const id = await resolveId(slug)
      if (!id) return
      const existing = cart.items.find((l) => l.slug === slug)?.qty ?? 0
      await setQty(id, existing + qty)
    },
    [cart.items, resolveId, setQty],
  )

  const removeItem = useCallback(
    async (productId: string) => {
      setBusy(true)
      try {
        const res = await fetch(`/api/cart/items/${productId}`, { method: 'DELETE' })
        if (res.ok) persist((await res.json()) as CartView)
      } finally {
        setBusy(false)
      }
    },
    [persist],
  )

  const clear = useCallback(async () => {
    setBusy(true)
    try {
      await fetch('/api/cart', { method: 'DELETE' })
      persist(EMPTY)
    } finally {
      setBusy(false)
    }
  }, [persist])

  const checkout = useCallback(
    async (payload: CheckoutPayload): Promise<CheckoutOutcome> => {
      setBusy(true)
      try {
        const res = await fetch('/api/cart/checkout', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        })
        // 401 → guest must log in before checking out.
        if (res.status === 401) return 'unauthorized'
        if (!res.ok) return null
        // The server keeps the cart cached (15-min payment window) — it's a
        // snapshot, not a flush — so we DON'T clear it locally; the basket
        // stays visible until it expires (or a real payment clears it later).
        return (await res.json()) as CheckoutResult
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  return (
    <CartContext.Provider
      value={{
        cart,
        ready,
        busy,
        isOpen,
        open,
        close,
        addBySlug,
        setQty,
        removeItem,
        clear,
        checkout,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart(): CartCtx {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
